import { useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import {
  advanceUpdateObject,
  changeObject,
  closeDrawer,
  deleteObject,
  findObjectsByIds,
  firstObjectDirtyFieldsSelector,
  firstObjectSelector,
  isFirstObjectSaveDisabledSelector,
  isSecondObjectSaveDisabledSelector,
  objectMapSelector,
  resetObject,
  secondObjectDirtyFieldsSelector,
  secondObjectSelector,
  updateObject,
} from 'slices/object.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TTEObject } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import ObjectManagerDetail from './ObjectManagerDetail';
import useFetchMissingItems from './hooks/useFetchMissingItems';

interface Props {
  confirmDiscardChanges: (isFirstDrawer: boolean, actionFn: () => void) => void;
}

const ObjectManagerDrawer = ({ confirmDiscardChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstObject = useAppSelector(firstObjectSelector);
  const secondObject = useAppSelector(secondObjectSelector);
  const firstObjectDirtyFields = useAppSelector(firstObjectDirtyFieldsSelector);
  const secondObjectDirtyFields = useAppSelector(
    secondObjectDirtyFieldsSelector
  );
  const firstObjectSaveDisabled = useAppSelector(
    isFirstObjectSaveDisabledSelector
  );
  const secondObjectSaveDisabled = useAppSelector(
    isSecondObjectSaveDisabledSelector
  );
  const objectMap = useAppSelector(objectMapSelector);

  const selectedObjectIds = useSelectResource(
    'integer',
    objectMap,
    firstObject,
    secondObject,
    (object) => dispatch(changeObject({ isFirstDrawer: true, object })),
    (object) => dispatch(changeObject({ isFirstDrawer: false, object }))
  );

  const itemIds = useMemo(
    () => selectedObjectIds.map(Number),
    [selectedObjectIds]
  );

  const fetchObjects = useCallback(
    (ids: number[]) => {
      dispatch(findObjectsByIds({ ids }));
    },
    [dispatch]
  );

  useFetchMissingItems<TTEObject>({
    itemIds,
    itemMap: objectMap,
    fetchItemsFn: fetchObjects,
  });

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardChanges(isFirstDrawer, () => {
        dispatch(closeDrawer({ isFirstDrawer }));
      }),
    [confirmDiscardChanges, dispatch]
  );

  const onChangeObject = useCallback(
    (isFirstDrawer: boolean, object: Partial<TTEObject>) => {
      dispatch(advanceUpdateObject({ isFirstDrawer, object }));
    },
    [dispatch]
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstObject?.extId,
        content: firstObject ? (
          <ObjectManagerDetail
            object={firstObject}
            onChange={(object) => onChangeObject(true, object)}
            dirtyFields={firstObjectDirtyFields}
          />
        ) : null,
        drawerOpen: !!firstObject,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('object_confirmation_delete')}
            handleDelete={() =>
              !!firstObject.id && dispatch(deleteObject(firstObject.id))
            }
            handleDiscard={() => dispatch(resetObject({ isFirstDrawer: true }))}
            handleSave={() => dispatch(updateObject(firstObject))}
            isSaveDisable={firstObjectSaveDisabled}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondObject?.extId,
        content: secondObject ? (
          <ObjectManagerDetail
            object={secondObject}
            onChange={(object) => onChangeObject(false, object)}
            dirtyFields={secondObjectDirtyFields}
          />
        ) : null,
        drawerOpen: !!secondObject,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('object_confirmation_delete')}
            handleDelete={() =>
              !!secondObject.id && dispatch(deleteObject(secondObject.id))
            }
            handleDiscard={() =>
              dispatch(resetObject({ isFirstDrawer: false }))
            }
            handleSave={() => dispatch(updateObject(secondObject))}
            isSaveDisable={secondObjectSaveDisabled}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default ObjectManagerDrawer;
